import { useEffect } from 'react';
import { Error } from '@mui/icons-material';
import { QueryClient, QueryClientProvider } from 'react-query';
import Slide from '@mui/material/Slide';
import { SnackbarProvider } from 'notistack';
import { Router, Redirect, Route, Switch } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { PrivateRoute, UserAgreement } from './components';
import { withTracker } from './services/ga';
import {
  AdminContextHandler,
  ManageVendorPageContextHandler,
  SideNavHandler,
  UserContextHandler,
  VendorPageContextHandler,
  LDWrapper,
  VersionWrapper,
  DocumentContextHandler
} from './context';
import {
  ACHNewNotificationPage,
  ACHPage,
  ActivityTracking,
  AddEditClient,
  AddEditJob,
  AddEditNeighborhood,
  AddEditOption,
  AddEditPlan,
  AdminLanding,
  BidManagement,
  BuildSelectedOptions,
  BuildSelectedOptionsDetail,
  BulkOptionExclusionsPage,
  BulkOptionInclusionsPage,
  BulkStandardFeaturesPage,
  CheckDetails,
  ChecklistDetailsPage,
  ChecklistReview,
  ChecklistReviewDetail,
  ChecklistTemplatesList,
  ClientsPage,
  CompactionTests,
  CompactionTestJobs,
  ClosingCalendarPage,
  ContentLibrariesPage,
  ContractAcceptanceDetail,
  ContractActivity,
  ContractDistribution,
  ContractManagementDetails,
  ContractManagment,
  ContractManagementPdfPOC,
  Contracts,
  CreateFieldWalkthroughPage,
  CustomPage,
  CustomPages,
  DelayStarts,
  DetailRole,
  DynamicFileManagementEditPage,
  ETLJobsPage,
  EditColorScheme,
  EditMaterial,
  EditPart,
  EmailTestsPage,
  FavoriteOrdersPage,
  FieldWalkthroughDetailPage,
  FieldWalkthroughsList,
  ForgotPassword,
  Home,
  InProgressContracts,
  InvoiceDetails,
  Invoices,
  JobBulkEditPage,
  Login,
  Logout,
  MaintenanceMode,
  ManageAdminUsers,
  ManageAttorneyOffices,
  ManageAuthorities,
  ManageBuilds,
  ManageChecklists,
  ManageColorSchemes,
  ManageContractTemplates,
  ManageCounties,
  ManageCustomPage,
  ManageEmailTemplatesPage,
  ManageFramers,
  ManageGroups,
  ManageJobsPage,
  ManageModelHomeTemplates,
  ManageMunicipalities,
  ManageNeighborhoodTemplates,
  ManageNeighborhoods,
  ManageOptions,
  ManagePartClasses,
  ManagePlanRooms,
  ManagePlanStyles,
  ManagePlanTemplates,
  ManagePlans,
  ManagePlantLists,
  ManagePunchList,
  ManageRoles,
  ManageSpecs,
  ManageStartTemplates,
  ManageTerritories,
  ManageTools,
  ManageTradePartners,
  ManageUndefinedParts,
  ManageUsers,
  ManageVendorDetail,
  ManageVendorsList,
  ModelHomePossessionsDetails,
  MyBuild,
  NeighborhoodPunchlists,
  NewPunchlist,
  NewsManagementPage,
  NewsPage,
  NotFound,
  OptionCategoryDescriptions,
  OrderDescriptions,
  OrderManagement,
  OrderManagementDetail,
  OrderTypes,
  Payments,
  PendingVendors,
  PlaceOrder,
  PlanBrochures,
  ProductionProtal,
  ProjectManagersPage,
  PunchlistDashboard,
  PunchlistDetails,
  PunchlistReportsPage,
  PunchlistSearch,
  PunchlistTemplates,
  PurchaseOrders,
  PurchaseOrdersDetails,
  ResetPassword,
  ResetSpecs,
  SageConnectivityStatusPage,
  SalesLanding,
  SalesManagement,
  SalesReportsPage,
  SelectedBuildOptions,
  SetsAndPalettes,
  SiteReportsList,
  SpecBuildOptions,
  StaffReassignment,
  StandardFeatures,
  StyleGuide,
  StyleGuideBulkDownloadGrid,
  StyleGuideButtons,
  StyleGuideCSVDownloadButton,
  StyleGuideCards,
  StyleGuideGridsOverview,
  StyleGuideLoader,
  StyleGuideModals,
  StyleGuidePageComponent,
  StyleGuidePageTitle,
  StyleGuidePageTitleWBack,
  StyleGuideUIUXPatterns,
  SupervisorsPage,
  TenDayBuildsDetail,
  TestBuildsPage,
  TodosPage,
  TradePartners,
  UserManagement,
  UserSettings,
  UsersPage,
  VendorContractsActivityPage,
  VendorPortal,
  VendorPunchlistsPage
} from './pages';
import { DevelopmentPortal } from './pages/development/DevelopmentPortal';
import { HomeownerPortal } from './pages/homeowner/homeowner-portal';
import {
  HomeownerPortalLanding,
  ReferralProgram,
  LearnMoreAboutMyHome,
  LearnMoreAboutMyCommunity,
  WarrantyPortal,
  PhasesOfConstruction
} from './pages/homeowner-portal';
import { SubmitPhotos } from './pages/homeowner-portal/submit-photos';
import {
  ExcludedColorsOptionsPage,
  ExcludedOptionsPage,
  SeriesDetailsPage,
  StandardFeaturesPage
} from './pages/development/manage-neighborhoods/options';
import { AddEditUser } from './pages/admin/unified-user-management/add-edit-user';
import { ManageRolesAndTitles } from './pages/admin/unified-user-management/manage-roles-and-titles';
import { AppRoleDetails } from './pages/admin/unified-user-management/manage-roles-and-titles/manage-app-roles/app-role-details';
import { JobTitleDetails } from './pages/admin/unified-user-management/manage-roles-and-titles/manage-job-titles/job-title-details';
import { MarketingMaterialsPage } from './pages/sales/marketing-materials';
import { ChangeSeries } from './pages/sales/change-series';
import { ContractMarkup } from './pages/sales/contract-markup';
import { TenDayBuildsPage } from './pages/sales/ten-day-builds/ten-day-builds-page';
import { ContractAcceptancePage } from './pages/sales/contract-acceptance/contract-acceptance-page';
import { FeatureFlag, UserRoles } from './models';
import { MaintenanceWrapper } from './context/maintenanceMode';
import { ManageParts } from './pages/development/manage-parts';
import { ManageMaterials } from './pages/development/manage-materials';
import { SetsAndPalettesDetails } from './pages/development/manage-options/sets-and-palettes/color-palette-sets/color-palette-sets-detail';
import { ManageModelHomeFurniture } from './pages/development/manage-possessions/manage-model-home-furniture';
import { SalesContextHandler } from './context/sales';
import { DocumentLanding } from './pages/documentation/document-landing';
import { NewsNotificationContextHandler } from './context/news-notification';
import { UnderContract } from './pages/sales';
import { UnderContractDetails } from './pages/sales/under-contract/under-contract-details';
import { WarrantyUsersPage } from './pages/development/warranty-users';
import { ForemenPage } from './pages/development/foremen';
import { BuildDocumentation } from './pages/documentation/build-documentation/build-documentation';
import { FileManagement } from './pages/documentation/file-management/file-management';
import {
  ListManagementDetail,
  ListManagementPage
} from './pages/production/field-walkthroughs/list-management';
import { BuildCommunications } from './pages/production/build-communications';
import { BuildCommunicationsEdit } from './pages/production/build-communications';
import { WarrantyMessages } from './pages/production/warranty-messages';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Hotjar from '@hotjar/browser';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from './appInsightsConfig';
import { GlobalContextHandler } from './context/global';
import { BuildDocumentationDetails } from './pages/documentation/build-documentation/build-documentation-details';
import { AdminPortal } from './helpers/links';
import { SALES_APP_SLUG } from './helpers';
import { browserHistory } from './appInsightsConfig';
import { HOMEOWNER_PORTAL_ROUTES } from './pages/homeowner-portal/routes';
import { BuildsContextHandler } from './pages/homeowner-portal/context';

const { isRabren, V2System, REACT_APP_GA_TRACKING, LegacySystem } = require('./buildSettings.json');

declare global {
  interface Window {
    ga: any | undefined;
  }
}
// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 0, // no caching of api calls,
      retry: false, // no retry on failed api calls
      retryOnMount: false, // the query will not be retried on mount if it contains an error
      staleTime: Infinity, // no caching of api calls
      refetchOnMount: false, // the query will not refetch on mount.
      refetchOnWindowFocus: false // the query will not refetch on window focus.
    }
  }
});

export default function App() {
  const siteId = 3875350;
  const hotjarVersion = 6;
  const initializeGoogleAnalytics = () => {
    if (!window.ga && REACT_APP_GA_TRACKING) {
      ReactGA.initialize(REACT_APP_GA_TRACKING, {
        gtagOptions: {
          titleCase: false
        }
      });
    }
  };

  useEffect(() => {
    initializeGoogleAnalytics();
    Hotjar.init(siteId, hotjarVersion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Generate manifest.json
  // build out the dynmic manifest.json for Android phones
  const myDynamicManifest = {
    short_name: 'BuilderLinq',
    name: isRabren === 'true' ? 'BuilderLinq - Rabren' : 'BuilderLinq',
    icons: [
      {
        src: isRabren === 'true' ? 'rabren/favicon.ico' : 'favicon.ico',
        sizes: '64x64 32x32 24x24 16x16',
        type: 'image/x-icon'
      },
      {
        src:
          isRabren === 'true' ? 'rabren/android-chrome-192x192.png' : 'android-chrome-192x192.png',
        type: 'image/png',
        sizes: '192x192'
      },
      {
        src:
          isRabren === 'true' ? 'rabren/android-chrome-512x512.png' : 'android-chrome-512x512.png',
        type: 'image/png',
        sizes: '512x512'
      }
    ],
    start_url: '.',
    display: 'standalone',
    theme_color: '#000000',
    background_color: '#ffffff',
    source: '/'
  };
  const link = document.createElement('link');
  link.rel = 'manifest';
  link.id = 'manifest';
  const stringManifest = JSON.stringify(myDynamicManifest);
  link.setAttribute(
    'href',
    'data:application/json;charset=utf-8,' + encodeURIComponent(stringManifest)
  );
  // set the new link in the <head> link
  document.head.appendChild(link);

  if (isRabren === 'true') {
    // Update Favicons
    const favicon = document.getElementById('favicon') as HTMLAnchorElement | null;
    favicon.href = `${V2System}/rabren/favicon.ico`;

    const favicon32 = document.getElementById('favicon-32') as HTMLAnchorElement | null;
    favicon32.href = `${V2System}/rabren/favicon-32x32.png`;

    const favicon16 = document.getElementById('favicon-16') as HTMLAnchorElement | null;
    favicon16.href = `${V2System}/rabren/favicon-16x16.png`;

    const appleTouchIcon = document.getElementById('apple-touch-icon') as HTMLAnchorElement | null;
    appleTouchIcon.href = `${V2System}/rabren/apple-touch-icon.png`;
  }
  if (!localStorage.getItem('stonemartin-nav-open')) {
    localStorage.setItem('stonemartin-nav-open', 'true');
  }
  const homeownerPortalRoles = [
    UserRoles.BLAdmin,
    UserRoles.BLDevelopment,
    UserRoles.BLField,
    UserRoles.BLFieldAdmin,
    UserRoles.BLFieldUserAdmin,
    UserRoles.BLFinance,
    UserRoles.BLProjectManager,
    UserRoles.BLReporting,
    UserRoles.BLSales,
    UserRoles.BLSalesAdmin,
    UserRoles.BLUserAdmin,
    UserRoles.BLWarranty,
    UserRoles.BillingAdmin,
    UserRoles.FinancialAdmin,
    UserRoles.ContentManager,
    UserRoles.GlobalAdmin,
    UserRoles.WarrantyAdmin,
    UserRoles.BLClient
  ];
  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <SnackbarProvider
        iconVariant={{
          error: <Error style={{ marginRight: '5px' }} />
        }}
        maxSnack={3}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        autoHideDuration={5000}
        TransitionComponent={Slide}
        dense={false}
      >
        <Router history={browserHistory}>
          <QueryClientProvider client={queryClient}>
            <MaintenanceWrapper>
              <UserContextHandler>
                <LDWrapper>
                  <GlobalContextHandler>
                    <NewsNotificationContextHandler>
                      <VersionWrapper>
                        <AdminContextHandler>
                          <SalesContextHandler>
                            <DocumentContextHandler>
                              <BuildsContextHandler>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                  <SideNavHandler>
                                    <Switch>
                                      {/* Auth */}
                                      <Route exact path="/login" component={withTracker(Login)} />
                                      <Route exact path="/logout" component={withTracker(Logout)} />
                                      <Route
                                        exact
                                        path="/forgot-password"
                                        component={withTracker(ForgotPassword)}
                                      />
                                      <Route
                                        exact
                                        path="/reset-password"
                                        component={withTracker(ResetPassword)}
                                      />
                                      <Route
                                        exact
                                        path="/create-password"
                                        component={withTracker(ResetPassword)}
                                      />
                                      {/* Home */}
                                      <PrivateRoute exact path="/">
                                        <Home />
                                      </PrivateRoute>
                                      {/* News */}
                                      <PrivateRoute exact path="/news">
                                        <NewsPage />
                                      </PrivateRoute>
                                      {/* redirect pages */}
                                      <PrivateRoute exact path="/trade-partners">
                                        <TradePartners />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/trade-partners/invoices-payments">
                                        <TradePartners />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/trade-partners/invoices/:invoiceNumber"
                                      >
                                        <TradePartners />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/trade-partners/payments/:checkNumber"
                                      >
                                        <TradePartners />
                                      </PrivateRoute>
                                      {/* Vendor Portal */}
                                      <PrivateRoute exact path="/vendors">
                                        <VendorPortal />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/vendors/invoices">
                                        <VendorPageContextHandler>
                                          <Invoices />
                                        </VendorPageContextHandler>
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/vendors/payments">
                                        <VendorPageContextHandler>
                                          <Payments />
                                        </VendorPageContextHandler>
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/vendors/purchase-orders">
                                        <VendorPageContextHandler>
                                          <PurchaseOrders />
                                        </VendorPageContextHandler>
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/vendors/purchase-orders/:purchaseOrderNumber"
                                      >
                                        <VendorPageContextHandler>
                                          <PurchaseOrdersDetails />
                                        </VendorPageContextHandler>
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/vendors/invoices/:invoiceNumber">
                                        <VendorPageContextHandler>
                                          <InvoiceDetails />
                                        </VendorPageContextHandler>
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/vendors/payments/:checkNumber">
                                        <VendorPageContextHandler>
                                          <CheckDetails />
                                        </VendorPageContextHandler>
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/vendors/bid-management">
                                        <VendorPageContextHandler>
                                          <BidManagement />
                                        </VendorPageContextHandler>
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/vendors/user-management">
                                        <VendorPageContextHandler>
                                          <UserManagement />
                                        </VendorPageContextHandler>
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/vendors/contracts">
                                        <VendorPageContextHandler>
                                          <Contracts />
                                        </VendorPageContextHandler>
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/vendors/contracts/in-progress">
                                        <VendorPageContextHandler>
                                          <InProgressContracts />
                                        </VendorPageContextHandler>
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/vendors/punchlists">
                                        <VendorPageContextHandler>
                                          <VendorPunchlistsPage />
                                        </VendorPageContextHandler>
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/vendors/punchlists/:punchlistId">
                                        <VendorPageContextHandler>
                                          <PunchlistDetails route="vendors" />
                                        </VendorPageContextHandler>
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/vendors/vendor-affiliates"
                                        roles={['BL_Admin', 'Global Admin']}
                                      >
                                        <VendorPageContextHandler>
                                          <ManageAdminUsers isVendorAffiliate />
                                        </VendorPageContextHandler>
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/vendors/compaction-tests"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.VendorCompactionManager,
                                          UserRoles.BLCompaction
                                        ]}
                                      >
                                        <CompactionTests />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/vendors/compaction-tests/:neighborhoodId"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.VendorCompactionManager,
                                          UserRoles.BLCompaction
                                        ]}
                                      >
                                        <CompactionTestJobs />
                                      </PrivateRoute>
                                      {/* Admin Portal */}
                                      <PrivateRoute
                                        exact
                                        path={AdminPortal.url}
                                        roles={AdminPortal.roles}
                                      >
                                        <AdminLanding />
                                      </PrivateRoute>
                                      {/* Admin Manage Links */}
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage-plant-lists"
                                        roles={[UserRoles.BLAdmin]}
                                      >
                                        <ManagePlantLists />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage/start-templates"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLStartSheetTemplates]}
                                        legacyUrl={`${LegacySystem}/Portals/Admin/StartTemplates.aspx`}
                                        featureFlag={FeatureFlag.v2ManageTools}
                                      >
                                        <ManageStartTemplates />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage/start-templates/neighborhoods"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLStartSheetTemplates]}
                                        legacyUrl={`${LegacySystem}/Portals/Admin/StartTemplates.aspx`}
                                        featureFlag={FeatureFlag.v2ManageTools}
                                      >
                                        <ManageNeighborhoodTemplates />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage/start-templates/model-homes"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLStartSheetTemplates]}
                                        legacyUrl={`${LegacySystem}/Portals/Admin/StartTemplates.aspx`}
                                        featureFlag={FeatureFlag.v2ManageTools}
                                      >
                                        <ManageModelHomeTemplates />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage/start-templates/plans"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLStartSheetTemplates]}
                                        legacyUrl={`${LegacySystem}/Portals/Admin/StartTemplates.aspx`}
                                        featureFlag={FeatureFlag.v2ManageTools}
                                      >
                                        <ManagePlanTemplates />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage/plan-styles"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                        legacyUrl={`${LegacySystem}/Portals/Development/ManagePlans.aspx`}
                                        featureFlag={FeatureFlag.v2ManageTools}
                                      >
                                        <ManagePlanStyles />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage/plan-rooms"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                        featureFlag={FeatureFlag.v2ManageTools}
                                      >
                                        <ManagePlanRooms />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage/counties"
                                        roles={[UserRoles.BLAdmin]}
                                        featureFlag={FeatureFlag.v2ManageTools}
                                      >
                                        <ManageCounties />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage/territories"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                        legacyUrl={`${LegacySystem}/Portals/Development/Territories.aspx`}
                                        featureFlag={FeatureFlag.v2ManageTools}
                                      >
                                        <ManageTerritories />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage/authorities"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                        legacyUrl={`${LegacySystem}/Portals/Development/ManageAuthorities.aspx`}
                                        featureFlag={FeatureFlag.v2ManageTools}
                                      >
                                        <ManageAuthorities />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage/municipalities"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                        legacyUrl={`${LegacySystem}/Portals/Development/ManageMunicipalities.aspx`}
                                        featureFlag={FeatureFlag.v2ManageTools}
                                      >
                                        <ManageMunicipalities />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage/etl-jobs"
                                        roles={[UserRoles.BLAdmin]}
                                        legacyUrl={`${LegacySystem}/Portals/Admin/EtlJobs.aspx`}
                                        featureFlag={FeatureFlag.v2ManageTools}
                                      >
                                        <ETLJobsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage/bulk-option-exclusions"
                                        roles={[UserRoles.BLAdmin]}
                                        featureFlag={FeatureFlag.bulkOptionExclusions}
                                      >
                                        <BulkOptionExclusionsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage/bulk-option-inclusions"
                                        roles={[UserRoles.BLAdmin]}
                                        featureFlag={FeatureFlag.bulkOptionExclusions}
                                      >
                                        <BulkOptionInclusionsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage/bulk-standard-features"
                                        roles={[UserRoles.BLAdmin]}
                                        featureFlag={FeatureFlag.bulkOptionExclusions}
                                      >
                                        <BulkStandardFeaturesPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage/sage-connectivity-status"
                                        featureFlag={FeatureFlag.v2ManageTools}
                                        roles={[UserRoles.GlobalAdmin]}
                                      >
                                        <SageConnectivityStatusPage />
                                      </PrivateRoute>
                                      {/* Admin vendor tools links */}
                                      <PrivateRoute
                                        exact
                                        path="/admin/vendor-tools/vendors"
                                        roles={[UserRoles.BLAdmin]}
                                      >
                                        <ManageVendorPageContextHandler>
                                          <ManageVendorsList />
                                        </ManageVendorPageContextHandler>
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/vendor-tools/vendors/:vendorPartnerId"
                                        roles={[UserRoles.BLAdmin]}
                                      >
                                        <ManageVendorPageContextHandler>
                                          <ManageVendorDetail />
                                        </ManageVendorPageContextHandler>
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/vendor-tools/contract-templates"
                                        featureFlag={FeatureFlag.v2ShowTradePartnerContracts}
                                        roles={[UserRoles.BLAdmin]}
                                      >
                                        <ManageContractTemplates />
                                      </PrivateRoute>

                                      <PrivateRoute
                                        exact
                                        path="/admin/vendor-tools/contract-activity"
                                        featureFlag={FeatureFlag.v2ShowTradePartnerContracts}
                                        roles={[UserRoles.BLAdmin]}
                                      >
                                        <ContractActivity />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/vendor-tools/pending-vendors"
                                        featureFlag={FeatureFlag.v2ShowTradePartnerContracts}
                                        roles={[UserRoles.BLAdmin]}
                                      >
                                        <PendingVendors />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/vendor-tools/contract-distribution"
                                        featureFlag={FeatureFlag.v2ShowTradePartnerContracts}
                                        roles={[UserRoles.BLAdmin]}
                                      >
                                        <ContractDistribution />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/vendor-tools/content-libraries"
                                        featureFlag={FeatureFlag.v2ShowTradePartnerContracts}
                                        roles={[UserRoles.BLAdmin]}
                                      >
                                        <ContentLibrariesPage />
                                      </PrivateRoute>
                                      {/* Admin Content Management Links */}
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage-content/custom-pages"
                                        roles={[UserRoles.BLAdmin, UserRoles.ContentManager]}
                                      >
                                        <CustomPages />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage-content/custom-pages/:slug"
                                        roles={[UserRoles.BLAdmin, UserRoles.ContentManager]}
                                      >
                                        <ManageCustomPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/manage-content/email-tests"
                                        roles={[UserRoles.BLAdmin, UserRoles.ContentManager]}
                                        legacyUrl={`${LegacySystem}/Portals/Admin/AdminTools.aspx`}
                                        featureFlag={FeatureFlag.v2ManageTools}
                                      >
                                        <EmailTestsPage />
                                      </PrivateRoute>

                                      <PrivateRoute
                                        path="/admin/manage-content/emails"
                                        roles={[UserRoles.BLAdmin, UserRoles.ContentManager]}
                                      >
                                        <ManageEmailTemplatesPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        path="/admin/manage-content/news"
                                        roles={[UserRoles.BLAdmin, UserRoles.ContentManager]}
                                      >
                                        <NewsManagementPage />
                                      </PrivateRoute>
                                      {/* Admin Build Links */}
                                      <PrivateRoute
                                        exact
                                        path="/admin/build/delay-starts"
                                        roles={[UserRoles.BLAdmin]}
                                        legacyUrl={`${LegacySystem}/Portals/Admin/AdminTools.aspx`}
                                        featureFlag={FeatureFlag.v2ShowBuildTools}
                                      >
                                        <DelayStarts />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/build/build-utilities"
                                        roles={[UserRoles.BLAdmin]}
                                        legacyUrl={`${LegacySystem}/Portals/Admin/AdminTools.aspx`}
                                        featureFlag={FeatureFlag.v2ShowBuildTools}
                                      >
                                        <ManageBuilds />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/build/punch-list"
                                        roles={[UserRoles.BLAdmin]}
                                        legacyUrl={`${LegacySystem}/Portals/Admin/AdminTools.aspx`}
                                        featureFlag={FeatureFlag.v2ShowBuildTools}
                                      >
                                        <ManagePunchList />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/build/reset-spec"
                                        legacyUrl={`${LegacySystem}/Portals/Admin/AdminTools.aspx`}
                                        roles={[UserRoles.BLAdmin]}
                                        featureFlag={FeatureFlag.v2ShowBuildTools}
                                      >
                                        <ResetSpecs />
                                      </PrivateRoute>
                                      {/* Admin Sales Tools Links */}
                                      <PrivateRoute
                                        exact
                                        path="/sales/sales-tools/option-category-descriptions"
                                      >
                                        <OptionCategoryDescriptions />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin,
                                          UserRoles.BLWarranty
                                        ]}
                                      >
                                        <ProductionProtal />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/build-communications"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin,
                                          UserRoles.BLWarranty
                                        ]}
                                        featureFlag={FeatureFlag.buildCommunicationsPage}
                                      >
                                        <BuildCommunications />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/build-communications/photo-upload/:buildId"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin,
                                          UserRoles.BLWarranty
                                        ]}
                                      >
                                        <BuildCommunicationsEdit />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/build-communications/homeowner-messaging/:buildId"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin,
                                          UserRoles.BLWarranty
                                        ]}
                                      >
                                        <BuildCommunicationsEdit />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/warranty-messages"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin,
                                          UserRoles.BLWarranty
                                        ]}
                                        featureFlag={FeatureFlag.warrantyQuestionPage}
                                      >
                                        <WarrantyMessages />
                                      </PrivateRoute>
                                      {/* Admin User Tools Links */}
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/users"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                      >
                                        <ManageAdminUsers />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                      >
                                        <UsersPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management/manage-roles-and-titles"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                        featureFlag={FeatureFlag.manageAppRolesPage}
                                      >
                                        <ManageRolesAndTitles />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management/manage-roles-and-titles/app-roles"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                        featureFlag={FeatureFlag.manageAppRolesPage}
                                      >
                                        <ManageRolesAndTitles />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management/manage-roles-and-titles/app-roles/:roleId"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                        featureFlag={FeatureFlag.manageAppRolesPage}
                                      >
                                        <AppRoleDetails />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management/manage-roles-and-titles/app-roles/add-role"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                        featureFlag={FeatureFlag.manageAppRolesPage}
                                      >
                                        <AppRoleDetails />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management/manage-roles-and-titles/job-titles"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                        featureFlag={FeatureFlag.manageAppRolesPage}
                                      >
                                        <ManageRolesAndTitles />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management/manage-roles-and-titles/job-titles/:id"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                      >
                                        <JobTitleDetails />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management/manage-roles-and-titles/job-titles/add-title"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                      >
                                        <JobTitleDetails />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management/:userId"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                      >
                                        <AddEditUser />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/attorney-offices"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                        legacyUrl={`${LegacySystem}/Portals/Users/Attorneys.aspx`}
                                      >
                                        <ManageAttorneyOffices />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/framers"
                                        roles={[UserRoles.BLAdmin]}
                                      >
                                        <ManageFramers />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/project-managers"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLFieldUserAdmin]}
                                      >
                                        <ProjectManagersPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/supervisors"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLFieldUserAdmin]}
                                      >
                                        <SupervisorsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/staff-reassignment"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLFieldUserAdmin]}
                                      >
                                        <StaffReassignment />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/warranty-managers"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLFieldUserAdmin]}
                                      >
                                        <WarrantyUsersPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/foremen"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLFieldUserAdmin]}
                                      >
                                        <ForemenPage />
                                      </PrivateRoute>
                                      {/* Order Links */}
                                      <PrivateRoute
                                        exact
                                        path="/production/orders/manage-roles"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BillingAdmin,
                                          UserRoles.FinancialAdmin
                                        ]}
                                      >
                                        <ManageRoles />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/orders/manage-roles/:id"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BillingAdmin,
                                          UserRoles.FinancialAdmin
                                        ]}
                                      >
                                        <DetailRole />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/orders/manage-users"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BillingAdmin,
                                          UserRoles.FinancialAdmin
                                        ]}
                                        featureFlag={FeatureFlag.hidePlaceOrderPage}
                                      >
                                        <ManageUsers />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/production/orders/place-order">
                                        <PlaceOrder />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/orders/order-descriptions"
                                      >
                                        <OrderDescriptions />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/production/orders/order-types">
                                        <OrderTypes />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/orders/order-management"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin,
                                          UserRoles.BLWarranty
                                        ]}
                                      >
                                        <OrderManagement />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/orders/order-management/:fieldOrderId"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin,
                                          UserRoles.BLWarranty
                                        ]}
                                      >
                                        <OrderManagementDetail />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/checklists/checklist-templates"
                                        legacyUrl={`${LegacySystem}/Portals/Production/POChecklists.aspx`}
                                        roles={['BL_Admin']}
                                        featureFlag={FeatureFlag.v2ProductionChecklists}
                                      >
                                        <ChecklistTemplatesList />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/checklists/manage-checklists"
                                        legacyUrl={`${LegacySystem}/Portals/Production/POChecklists.aspx`}
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin
                                        ]}
                                        featureFlag={FeatureFlag.v2ProductionChecklists}
                                      >
                                        <ManageChecklists />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/checklists/manage-checklists/:purchaseOrderId"
                                        legacyUrl={`${LegacySystem}/Portals/Production/POChecklists.aspx`}
                                        roles={['BL_Admin', 'BL_Field', 'BL_FieldAdmin']}
                                        featureFlag={FeatureFlag.v2ProductionChecklists}
                                      >
                                        <ChecklistDetailsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/checklists/checklist-review"
                                        legacyUrl={`${LegacySystem}/Portals/Production/POChecklistReview.aspx`}
                                        roles={['BL_Admin', 'BL_Field', 'BL_FieldAdmin']}
                                        featureFlag={FeatureFlag.v2ProductionChecklists}
                                      >
                                        <ChecklistReview />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/checklists/checklist-review/:purchaseOrderId"
                                        legacyUrl={`${LegacySystem}/Portals/Production/POChecklistReview.aspx`}
                                        roles={['BL_Admin', 'BL_Field', 'BL_FieldAdmin']}
                                        featureFlag={FeatureFlag.v2ProductionChecklists}
                                      >
                                        <ChecklistReviewDetail />
                                      </PrivateRoute>
                                      {/* redirect pages */}
                                      <PrivateRoute exact path="/admin/manage/trade-partners">
                                        <ManageTradePartners />
                                      </PrivateRoute>
                                      <Redirect
                                        from="/admin/manage/contract-templates"
                                        to="/admin/vendor-tools/contract-templates"
                                      />
                                      <Redirect
                                        from="/admin/manage/executed-contracts"
                                        to="/admin/vendor-tools/contract-activity"
                                      />
                                      <Redirect
                                        from="/admin/manage/pending-vendors"
                                        to="/admin/vendor-tools/pending-vendors"
                                      />
                                      <Redirect
                                        from="/admin/manage/vendors"
                                        to="/admin/vendor-tools/vendors"
                                      />
                                      {/* START Homeowner portal */}
                                      <PrivateRoute
                                        exact
                                        pageIsInternalOnly
                                        path={`/custom-pages/${SALES_APP_SLUG}`}
                                      >
                                        <CustomPage pageSlug={SALES_APP_SLUG} />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/custom-pages/:slug">
                                        <CustomPage />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/homeowner-portals">
                                        <HomeownerPortal />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path={HOMEOWNER_PORTAL_ROUTES.LANDING}
                                        featureFlag={FeatureFlag.homeownerPortalv2}
                                        roles={homeownerPortalRoles}
                                      >
                                        <HomeownerPortalLanding />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path={HOMEOWNER_PORTAL_ROUTES.ABOUT_MY_HOME}
                                        featureFlag={FeatureFlag.homeownerPortalv2}
                                        roles={homeownerPortalRoles}
                                      >
                                        <LearnMoreAboutMyHome />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path={HOMEOWNER_PORTAL_ROUTES.LEARN_MORE_COMMUNITY}
                                        featureFlag={FeatureFlag.homeownerPortalv2}
                                        roles={homeownerPortalRoles}
                                      >
                                        <LearnMoreAboutMyCommunity />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path={HOMEOWNER_PORTAL_ROUTES.SUBMIT_PHOTOS}
                                        featureFlag={FeatureFlag.homeownerPortalv2}
                                        roles={homeownerPortalRoles}
                                      >
                                        <SubmitPhotos />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path={HOMEOWNER_PORTAL_ROUTES.WARRANTY_PORTAL}
                                        featureFlag={FeatureFlag.homeownerPortalv2}
                                        roles={homeownerPortalRoles}
                                      >
                                        <WarrantyPortal />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        path={HOMEOWNER_PORTAL_ROUTES.REFERRAL}
                                        featureFlag={FeatureFlag.homeownerPortalv2}
                                        roles={homeownerPortalRoles}
                                      >
                                        <ReferralProgram />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        path={HOMEOWNER_PORTAL_ROUTES.CONSTRUCTION_PHASES}
                                        featureFlag={FeatureFlag.homeownerPortalv2}
                                        roles={homeownerPortalRoles}
                                      >
                                        <PhasesOfConstruction />
                                      </PrivateRoute>
                                      {/* END Homeowner portal */}
                                      <PrivateRoute
                                        exact
                                        path="/my-build"
                                        featureFlag={FeatureFlag.clientViewBuild}
                                        legacyUrl={`${LegacySystem}/Portals/Sales/BuildOptions.aspx`}
                                      >
                                        <MyBuild />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/my-build/:id"
                                        roles={[UserRoles.BLClient]}
                                        featureFlag={FeatureFlag.clientViewBuild}
                                        legacyUrl={`${LegacySystem}/Portals/Sales/BuildOptions.aspx`}
                                      >
                                        <SpecBuildOptions path="/my-build" />
                                      </PrivateRoute>
                                      {/* Admin Financial Links */}
                                      <PrivateRoute
                                        exact
                                        path="/admin/financial/ach"
                                        roles={[UserRoles.BLFinancialTools]}
                                      >
                                        <ACHPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/financial/ach/new-notification"
                                        roles={[UserRoles.BLFinancialTools]}
                                      >
                                        <ACHNewNotificationPage />
                                      </PrivateRoute>
                                      {/* Documentation Links */}
                                      <PrivateRoute exact path="/documentation">
                                        <DocumentLanding />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/documentation/build-documentation"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin,
                                          UserRoles.BLClosing,
                                          UserRoles.BLSales,
                                          UserRoles.BLSalesAdmin,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <BuildDocumentation />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/documentation/build-documentation/file-management"
                                        combinationRoles={[UserRoles.BLAdmin, UserRoles.BLClosing]}
                                        globalAdminOverride
                                      >
                                        <FileManagement />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/documentation/build-documentation/file-management/dynamic/:pandaDocTemplateId"
                                        combinationRoles={[UserRoles.BLAdmin, UserRoles.BLClosing]}
                                        globalAdminOverride
                                      >
                                        <DynamicFileManagementEditPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/documentation/build-documentation/manage-document-groups"
                                        combinationRoles={[UserRoles.BLAdmin, UserRoles.BLClosing]}
                                        globalAdminOverride
                                      >
                                        <ManageGroups />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/documentation/build-documentation/:buildId"
                                      >
                                        <BuildDocumentationDetails />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/documentation/build-selected-options"
                                      >
                                        <BuildSelectedOptions />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/documentation/build-selected-options/:neighborhoodId/:lot"
                                      >
                                        <BuildSelectedOptionsDetail />
                                      </PrivateRoute>
                                      {/* Reporting links */}
                                      <PrivateRoute
                                        exact
                                        pageIsMemberOnly
                                        path="/reporting/undefined-parts"
                                      >
                                        <ManageUndefinedParts />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        pageIsMemberOnly
                                        exact
                                        path="/reporting/vendor-contract-activity"
                                      >
                                        <VendorContractsActivityPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        pageIsMemberOnly
                                        path="/reporting/activity-tracking"
                                        roles={[
                                          UserRoles.GlobalAdmin,
                                          UserRoles.BLActivityTracking
                                        ]}
                                      >
                                        <ActivityTracking />
                                      </PrivateRoute>

                                      <PrivateRoute
                                        exact
                                        path="/production"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin,
                                          UserRoles.BLWarranty
                                        ]}
                                      >
                                        <ProductionProtal />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/build-communications"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin,
                                          UserRoles.BLWarranty
                                        ]}
                                        featureFlag={FeatureFlag.buildCommunicationsPage}
                                      >
                                        <BuildCommunications />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/build-communications/photo-upload/:buildId"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin,
                                          UserRoles.BLWarranty
                                        ]}
                                      >
                                        <BuildCommunicationsEdit />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/build-communications/homeowner-messaging/:buildId"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin,
                                          UserRoles.BLWarranty
                                        ]}
                                      >
                                        <BuildCommunicationsEdit />
                                      </PrivateRoute>
                                      {/* Admin User Tools Links */}
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/users"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                      >
                                        <ManageAdminUsers />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/users/internal"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                      >
                                        <ManageAdminUsers />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/users/external"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                      >
                                        <ManageAdminUsers />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                      >
                                        <UsersPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management/manage-roles-and-titles"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                        featureFlag={FeatureFlag.manageAppRolesPage}
                                      >
                                        <ManageRolesAndTitles />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management/manage-roles-and-titles/app-roles"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                        featureFlag={FeatureFlag.manageAppRolesPage}
                                      >
                                        <ManageRolesAndTitles />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management/manage-roles-and-titles/app-roles/:roleId"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                        featureFlag={FeatureFlag.manageAppRolesPage}
                                      >
                                        <AppRoleDetails />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management/manage-roles-and-titles/app-roles/add-role"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                        featureFlag={FeatureFlag.manageAppRolesPage}
                                      >
                                        <AppRoleDetails />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management/manage-roles-and-titles/job-titles"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                        featureFlag={FeatureFlag.manageAppRolesPage}
                                      >
                                        <ManageRolesAndTitles />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management/manage-roles-and-titles/job-titles/:id"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                      >
                                        <JobTitleDetails />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management/manage-roles-and-titles/job-titles/add-title"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                      >
                                        <JobTitleDetails />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/unified-user-management/:userId"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                      >
                                        <AddEditUser />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/attorney-offices"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLUserAdmin]}
                                        legacyUrl={`${LegacySystem}/Portals/Users/Attorneys.aspx`}
                                      >
                                        <ManageAttorneyOffices />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/framers"
                                        roles={[UserRoles.BLAdmin]}
                                      >
                                        <ManageFramers />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/project-managers"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLFieldUserAdmin]}
                                      >
                                        <ProjectManagersPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/supervisors"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLFieldUserAdmin]}
                                      >
                                        <SupervisorsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/staff-reassignment"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLFieldUserAdmin]}
                                      >
                                        <StaffReassignment />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/warranty-managers"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLFieldUserAdmin]}
                                      >
                                        <WarrantyUsersPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/user-tools/foremen"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLFieldUserAdmin]}
                                      >
                                        <ForemenPage />
                                      </PrivateRoute>
                                      {/* Order Links */}
                                      <PrivateRoute
                                        exact
                                        path="/production/orders/manage-roles"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BillingAdmin,
                                          UserRoles.FinancialAdmin
                                        ]}
                                      >
                                        <ManageRoles />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/orders/manage-roles/:id"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BillingAdmin,
                                          UserRoles.FinancialAdmin
                                        ]}
                                      >
                                        <DetailRole />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/orders/manage-users"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BillingAdmin,
                                          UserRoles.FinancialAdmin
                                        ]}
                                        featureFlag={FeatureFlag.hidePlaceOrderPage}
                                      >
                                        <ManageUsers />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/production/orders/place-order">
                                        <PlaceOrder />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/orders/order-descriptions"
                                      >
                                        <OrderDescriptions />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/production/orders/order-types">
                                        <OrderTypes />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/orders/order-management"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin,
                                          UserRoles.BLWarranty
                                        ]}
                                      >
                                        <OrderManagement />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/orders/order-management/:fieldOrderId"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin,
                                          UserRoles.BLWarranty
                                        ]}
                                      >
                                        <OrderManagementDetail />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/checklists/checklist-templates"
                                        legacyUrl={`${LegacySystem}/Portals/Production/POChecklists.aspx`}
                                        roles={['BL_Admin']}
                                        featureFlag={FeatureFlag.v2ProductionChecklists}
                                      >
                                        <ChecklistTemplatesList />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/checklists/manage-checklists"
                                        legacyUrl={`${LegacySystem}/Portals/Production/POChecklists.aspx`}
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin
                                        ]}
                                        featureFlag={FeatureFlag.v2ProductionChecklists}
                                      >
                                        <ManageChecklists />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/checklists/manage-checklists/:purchaseOrderId"
                                        legacyUrl={`${LegacySystem}/Portals/Production/POChecklists.aspx`}
                                        roles={['BL_Admin', 'BL_Field', 'BL_FieldAdmin']}
                                        featureFlag={FeatureFlag.v2ProductionChecklists}
                                      >
                                        <ChecklistDetailsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/checklists/checklist-review"
                                        legacyUrl={`${LegacySystem}/Portals/Production/POChecklistReview.aspx`}
                                        roles={['BL_Admin', 'BL_Field', 'BL_FieldAdmin']}
                                        featureFlag={FeatureFlag.v2ProductionChecklists}
                                      >
                                        <ChecklistReview />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/checklists/checklist-review/:purchaseOrderId"
                                        legacyUrl={`${LegacySystem}/Portals/Production/POChecklistReview.aspx`}
                                        roles={['BL_Admin', 'BL_Field', 'BL_FieldAdmin']}
                                        featureFlag={FeatureFlag.v2ProductionChecklists}
                                      >
                                        <ChecklistReviewDetail />
                                      </PrivateRoute>
                                      {/* redirect pages */}
                                      <PrivateRoute exact path="/admin/manage/trade-partners">
                                        <ManageTradePartners />
                                      </PrivateRoute>
                                      <Redirect
                                        from="/admin/manage/contract-templates"
                                        to="/admin/vendor-tools/contract-templates"
                                      />
                                      <Redirect
                                        from="/admin/manage/executed-contracts"
                                        to="/admin/vendor-tools/contract-activity"
                                      />
                                      <Redirect
                                        from="/admin/manage/pending-vendors"
                                        to="/admin/vendor-tools/pending-vendors"
                                      />
                                      <Redirect
                                        from="/admin/manage/vendors"
                                        to="/admin/vendor-tools/vendors"
                                      />
                                      {/* START Homeowner portal */}
                                      <PrivateRoute
                                        exact
                                        pageIsInternalOnly
                                        path={`/custom-pages/${SALES_APP_SLUG}`}
                                      >
                                        <CustomPage pageSlug={SALES_APP_SLUG} />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/custom-pages/:slug">
                                        <CustomPage />
                                      </PrivateRoute>

                                      <PrivateRoute exact path="/homeowner-portals">
                                        <HomeownerPortal />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path={HOMEOWNER_PORTAL_ROUTES.LANDING}
                                        featureFlag={FeatureFlag.homeownerPortalv2}
                                        roles={homeownerPortalRoles}
                                      >
                                        <HomeownerPortalLanding />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path={HOMEOWNER_PORTAL_ROUTES.ABOUT_MY_HOME}
                                        featureFlag={FeatureFlag.homeownerPortalv2}
                                        roles={homeownerPortalRoles}
                                      >
                                        <LearnMoreAboutMyHome />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path={HOMEOWNER_PORTAL_ROUTES.LEARN_MORE_COMMUNITY}
                                        featureFlag={FeatureFlag.homeownerPortalv2}
                                        roles={homeownerPortalRoles}
                                      >
                                        <LearnMoreAboutMyCommunity />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path={HOMEOWNER_PORTAL_ROUTES.SUBMIT_PHOTOS}
                                        featureFlag={FeatureFlag.homeownerPortalv2}
                                        roles={homeownerPortalRoles}
                                      >
                                        <SubmitPhotos />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path={HOMEOWNER_PORTAL_ROUTES.WARRANTY_PORTAL}
                                        featureFlag={FeatureFlag.homeownerPortalv2}
                                        roles={homeownerPortalRoles}
                                      >
                                        <WarrantyPortal />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        path={HOMEOWNER_PORTAL_ROUTES.REFERRAL}
                                        featureFlag={FeatureFlag.homeownerPortalv2}
                                        roles={homeownerPortalRoles}
                                      >
                                        <ReferralProgram />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        path={HOMEOWNER_PORTAL_ROUTES.CONSTRUCTION_PHASES}
                                        featureFlag={FeatureFlag.homeownerPortalv2}
                                        roles={homeownerPortalRoles}
                                      >
                                        <PhasesOfConstruction />
                                      </PrivateRoute>
                                      {/* END Homeowner portal */}
                                      <PrivateRoute
                                        exact
                                        path="/my-build"
                                        featureFlag={FeatureFlag.clientViewBuild}
                                        legacyUrl={`${LegacySystem}/Portals/Sales/BuildOptions.aspx`}
                                      >
                                        <MyBuild />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/my-build/:id"
                                        roles={[UserRoles.BLClient]}
                                        featureFlag={FeatureFlag.clientViewBuild}
                                        legacyUrl={`${LegacySystem}/Portals/Sales/BuildOptions.aspx`}
                                      >
                                        <SpecBuildOptions path="/my-build" />
                                      </PrivateRoute>
                                      {/* Admin Financial Links */}
                                      <PrivateRoute
                                        exact
                                        path="/admin/financial/ach"
                                        roles={[UserRoles.BLFinancialTools]}
                                      >
                                        <ACHPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/admin/financial/ach/new-notification"
                                        roles={[UserRoles.BLFinancialTools]}
                                      >
                                        <ACHNewNotificationPage />
                                      </PrivateRoute>
                                      {/* Documentation Links */}
                                      <PrivateRoute exact path="/documentation">
                                        <DocumentLanding />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/documentation/build-documentation"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin,
                                          UserRoles.BLClosing,
                                          UserRoles.BLSales,
                                          UserRoles.BLSalesAdmin,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <BuildDocumentation />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/documentation/build-documentation/file-management"
                                        combinationRoles={[UserRoles.BLAdmin, UserRoles.BLClosing]}
                                        globalAdminOverride
                                      >
                                        <FileManagement />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/documentation/build-documentation/file-management/dynamic/:pandaDocTemplateId"
                                        combinationRoles={[UserRoles.BLAdmin, UserRoles.BLClosing]}
                                        globalAdminOverride
                                      >
                                        <DynamicFileManagementEditPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/documentation/build-documentation/manage-document-groups"
                                        combinationRoles={[UserRoles.BLAdmin, UserRoles.BLClosing]}
                                        globalAdminOverride
                                      >
                                        <ManageGroups />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/documentation/build-documentation/:buildId"
                                      >
                                        <BuildDocumentationDetails />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/documentation/build-selected-options"
                                      >
                                        <BuildSelectedOptions />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/documentation/build-selected-options/:neighborhoodId/:lot"
                                      >
                                        <BuildSelectedOptionsDetail />
                                      </PrivateRoute>
                                      {/* Reporting links */}
                                      <PrivateRoute
                                        exact
                                        pageIsMemberOnly
                                        path="/reporting/undefined-parts"
                                      >
                                        <ManageUndefinedParts />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        pageIsMemberOnly
                                        exact
                                        path="/reporting/vendor-contract-activity"
                                      >
                                        <VendorContractsActivityPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        pageIsMemberOnly
                                        path="/reporting/activity-tracking"
                                        roles={[
                                          UserRoles.GlobalAdmin,
                                          UserRoles.BLActivityTracking
                                        ]}
                                      >
                                        <ActivityTracking />
                                      </PrivateRoute>

                                      {/* Used for testing emails and how they look */}
                                      {/* <PrivateRoute
                                      exact
                                      path="/emails/new-user"
                                      component={NewUserEmail}
                                    >
                                      <NewUserEmail />
                                    </PrivateRoute> */}
                                      {/* Punchlist links */}
                                      <PrivateRoute
                                        exact
                                        path="/production/punchlists/dashboard"
                                        roles={['BL_Admin', 'BL_Field', 'BL_FieldAdmin']}
                                        featureFlag={FeatureFlag.v2ProductionPunchlists}
                                      >
                                        <PunchlistDashboard />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/punchlists/neighborhood-punchlists"
                                        roles={['BL_Admin', 'BL_Field', 'BL_FieldAdmin']}
                                        featureFlag={FeatureFlag.v2ProductionPunchlists}
                                      >
                                        <NeighborhoodPunchlists />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/punchlists/create-punchlist"
                                        roles={['BL_Admin', 'BL_Field', 'BL_FieldAdmin']}
                                        featureFlag={FeatureFlag.v2ProductionPunchlists}
                                      >
                                        <NewPunchlist />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/punchlists/search"
                                        roles={['BL_Admin', 'BL_Field', 'BL_FieldAdmin']}
                                        featureFlag={FeatureFlag.v2ProductionPunchlists}
                                      >
                                        <PunchlistSearch />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/punchlists/reports"
                                        roles={['BL_Admin', 'BL_Field', 'BL_FieldAdmin']}
                                        featureFlag={FeatureFlag.v2ProductionPunchlists}
                                      >
                                        <PunchlistReportsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/punchlists/todos"
                                        roles={['BL_Admin', 'BL_Field', 'BL_FieldAdmin']}
                                        featureFlag={FeatureFlag.v2ProductionPunchlists}
                                      >
                                        <TodosPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/punchlists/punchlist-templates"
                                        roles={['BL_Admin', 'BL_Field', 'BL_FieldAdmin']}
                                        featureFlag={FeatureFlag.v2ProductionPunchlists}
                                      >
                                        <PunchlistTemplates />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/punchlists/:punchlistId"
                                        roles={['BL_Admin', 'BL_Field', 'BL_FieldAdmin']}
                                        featureFlag={FeatureFlag.v2ProductionPunchlists}
                                      >
                                        <PunchlistDetails />
                                      </PrivateRoute>
                                      {/* Field Walkthrough links */}
                                      <PrivateRoute
                                        exact
                                        path="/production/field-walkthroughs"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLField,
                                          UserRoles.BLFieldAdmin,
                                          UserRoles.BLFieldUserAdmin
                                        ]}
                                        featureFlag={FeatureFlag.v2FieldWalkthroughs}
                                        legacyUrl={`${LegacySystem}/Portals/Production/FieldWalkthroughs.aspx`}
                                      >
                                        <FieldWalkthroughsList />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/field-walkthroughs/list-management"
                                        roles={[UserRoles.BLFieldUserAdmin]}
                                        legacyUrl={`${LegacySystem}/Portals/Production/FieldWalkthroughs.aspx`}
                                      >
                                        <ListManagementPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/field-walkthroughs/list-management/:fieldWalkthroughLocationId"
                                        roles={[UserRoles.BLFieldUserAdmin]}
                                        legacyUrl={`${LegacySystem}/Portals/Production/FieldWalkthroughs.aspx`}
                                      >
                                        <ListManagementDetail />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/field-walkthroughs/new"
                                        roles={['BL_Admin', 'BL_Field', 'BL_FieldAdmin']}
                                        featureFlag={FeatureFlag.v2FieldWalkthroughs}
                                        legacyUrl={`${LegacySystem}/Portals/Production/FieldWalkthroughs.aspx`}
                                      >
                                        <CreateFieldWalkthroughPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/production/field-walkthroughs/:fieldWalkthroughId"
                                        roles={['BL_Admin', 'BL_Field', 'BL_FieldAdmin']}
                                        featureFlag={FeatureFlag.v2FieldWalkthroughs}
                                        legacyUrl={`${LegacySystem}/Portals/Production/FieldWalkthroughs.aspx`}
                                      >
                                        <FieldWalkthroughDetailPage />
                                      </PrivateRoute>
                                      {/* Site Reports link */}
                                      <PrivateRoute
                                        exact
                                        path="/production/site-reports"
                                        featureFlag={FeatureFlag.v2SiteReports}
                                        legacyUrl={`${LegacySystem}/Portals/Production/SiteReports.aspx`}
                                      >
                                        <SiteReportsList />
                                      </PrivateRoute>
                                      {/* development section */}
                                      <PrivateRoute
                                        exact
                                        path="/development"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLFieldUserAdmin
                                        ]}
                                      >
                                        <DevelopmentPortal />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/options/manage-options"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <ManageOptions />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/parts/manage-parts"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <ManageParts />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/parts/manage-parts/:partId/general-information"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <EditPart />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/parts/manage-materials"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <ManageMaterials />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/parts/manage-materials/:materialId/general-information"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <EditMaterial />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/parts/manage-part-classes"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <ManagePartClasses />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/options/standard-features"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <StandardFeatures />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/options/option-sets-and-palettes/option-sets"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <SetsAndPalettes />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/options/option-sets-and-palettes/standard-feature-sets"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <SetsAndPalettes />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/options/option-sets-and-palettes/color-palette-sets"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <SetsAndPalettes />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/options/option-sets-and-palettes/color-palette-sets/:colorPaletteId"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <SetsAndPalettesDetails />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/plans/manage-plans"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <ManagePlans />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/possessions/manage-model-home-furniture"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <ManageModelHomeFurniture />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/possessions/manage-model-home-furniture/:possessionId"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <ModelHomePossessionsDetails />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/possessions/manage-tools"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <ManageTools />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/development/color-schemes">
                                        <ManageColorSchemes />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/color-schemes/:colorSchemeId/general-information"
                                      >
                                        <EditColorScheme />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/jobs"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <ManageJobsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/jobs/bulk-edit"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <JobBulkEditPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/jobs/:jobId/general-information"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <AddEditJob />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/jobs/:jobId/lot-information"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <AddEditJob />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/jobs/:jobId/site-prep"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <AddEditJob />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/jobs/:jobId/house-information"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <AddEditJob />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/jobs/:jobId/utility-municipality-information"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <AddEditJob />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/jobs/:jobId/finance-information"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <AddEditJob />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/jobs/:jobId/contract-information"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <AddEditJob />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/options/:optionId/general-information"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <AddEditOption />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/plans/plan-brochures"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <PlanBrochures />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/plans/manage-plans/:planId/general-information"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <AddEditPlan />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/plans/manage-plans/:planId/plan-details"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <AddEditPlan />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/plans/manage-plans/:planId/elevation-details"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <AddEditPlan />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/plans/manage-plans/:planId/plan-rooms"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <AddEditPlan />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/neighborhoods"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <ManageNeighborhoods />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/neighborhoods/:neighborhoodId/general-information"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <AddEditNeighborhood />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/neighborhoods/:neighborhoodId/sales-and-contracts"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <AddEditNeighborhood />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/neighborhoods/:neighborhoodId/hoa-information"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <AddEditNeighborhood />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/neighborhoods/:neighborhoodId/house-specifications"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <AddEditNeighborhood />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/neighborhoods/:neighborhoodId/amenities"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <AddEditNeighborhood />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/neighborhoods/:neighborhoodId/options"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <AddEditNeighborhood />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/neighborhoods/:neighborhoodId/options/:selectedSeriesId"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <SeriesDetailsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/neighborhoods/:neighborhoodId/options/:selectedSeriesId/standard-features"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <StandardFeaturesPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/neighborhoods/:neighborhoodId/options/:selectedSeriesId/excluded-options"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <ExcludedOptionsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/neighborhoods/:neighborhoodId/options/:selectedSeriesId/excluded-colors"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <ExcludedColorsOptionsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/neighborhoods/:neighborhoodId/supervisor-pm"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <AddEditNeighborhood />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/neighborhoods/:neighborhoodId/plat-maps"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <AddEditNeighborhood />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/specs"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <ManageSpecs />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/specs/:id"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLClient
                                        ]}
                                      >
                                        <SpecBuildOptions />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/specs/:id/selected-options"
                                        roles={[UserRoles.BLAdmin, UserRoles.BLDevelopment]}
                                      >
                                        <SelectedBuildOptions />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/project-managers"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLFieldUserAdmin
                                        ]}
                                      >
                                        <ProjectManagersPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/staff-reassignment"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLFieldUserAdmin
                                        ]}
                                      >
                                        <StaffReassignment />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/development/supervisors"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLDevelopment,
                                          UserRoles.BLFieldUserAdmin
                                        ]}
                                      >
                                        <SupervisorsPage />
                                      </PrivateRoute>
                                      {/* User Pages */}
                                      <PrivateRoute exact path="/user-settings">
                                        <UserSettings />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/favorite-orders">
                                        <FavoriteOrdersPage />
                                      </PrivateRoute>
                                      {/* Sales Pages */}
                                      <PrivateRoute
                                        exact
                                        path="/sales"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                      >
                                        <SalesLanding />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/dashboard"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                      >
                                        <SalesLanding />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/sales-tools/marketing"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                        roles={['BL_Admin', 'BL_Sales']}
                                      >
                                        <MarketingMaterialsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/sales-tools/change-series"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesAdminChangeNeighborhoodSeries.aspx`}
                                        roles={['BL_Admin', 'BL_Sales']}
                                      >
                                        <ChangeSeries />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/contracting/contract-markup"
                                        // legacyUrl={`${LegacySystem}/Portals/Sales/SalesAdminChangeNeighborhoodSeries.aspx`}
                                        roles={['BL_Admin', 'BL_Sales']}
                                      >
                                        <ContractMarkup />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/contracting/purchasers"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/ManageClients.aspx`}
                                        roles={['BL_Admin', 'BL_Client', 'BL_Sales']}
                                      >
                                        <ClientsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        path="/sales/contracting/purchasers/:personId"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                        roles={['BL_Admin', 'BL_Client', 'BL_Sales']}
                                      >
                                        <AddEditClient />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/sales-tools/specs"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                        roles={['BL_Admin', 'BL_Sales']}
                                      >
                                        <ManageSpecs
                                          backText="Back to Sales"
                                          path="/sales/sales-tools"
                                        />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/sales-tools/sales-management"
                                        roles={['BL_Admin', 'BL_SalesAdmin']}
                                      >
                                        <SalesManagement />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/sales-tools/contract-management"
                                        roles={['BL_Admin', 'BL_Sales']}
                                      >
                                        <ContractManagment />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/sales-tools/contract-management/viewer-poc"
                                        roles={['BL_Admin', 'BL_Sales']}
                                      >
                                        <ContractManagementPdfPOC />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/sales-tools/contract-management/:buildDocumentTypeId/:pandaDocTemplateId"
                                        roles={['BL_Admin', 'BL_Sales']}
                                      >
                                        <ContractManagementDetails />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/sales-tools/build-options/:id"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLSalesAdmin,
                                          UserRoles.BLSales,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <SpecBuildOptions
                                          path="/sales/sales-tools"
                                          shouldShowFinishSpecButton={false}
                                          pageTitle={'Build Options'}
                                        />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/contracting/build-options/:id"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLSalesAdmin,
                                          UserRoles.BLSales,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <SpecBuildOptions
                                          path="/sales/sales-tools"
                                          shouldShowFinishSpecButton={false}
                                          pageTitle={'Build Options'}
                                        />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/contracting/specs/:id"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                        roles={['BL_Admin', 'BL_Sales']}
                                      >
                                        <SpecBuildOptions path="/sales/sales-tools" />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/sales-tools/specs/:id"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                        roles={['BL_Admin', 'BL_Sales']}
                                      >
                                        <SpecBuildOptions path="/sales/sales-tools" />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/sales-tools/reports/build-selected-options/:neighborhoodId/:lot"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                        roles={['BL_Admin', 'BL_Sales']}
                                      >
                                        <BuildSelectedOptionsDetail
                                          backText="Back to Report"
                                          path="/sales/sales-tools/reports/build-selected-options"
                                        />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/sales-tools/build-options/:id/selected-options"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLSales,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <SelectedBuildOptions
                                          pageTitle={'Selected Build Options'}
                                          path="/sales/sales-tools"
                                        />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/sales-tools/specs/:id/selected-options"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                        roles={['BL_Admin', 'BL_Sales']}
                                      >
                                        <SelectedBuildOptions path="/sales/sales-tools" />
                                      </PrivateRoute>
                                      <PrivateRoute path="/sales/sales-tools/reports">
                                        <SalesReportsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/contracting/closing-calendar"
                                        featureFlag={FeatureFlag.salesClosingCalendar}
                                        roles={[
                                          'BL_Admin',
                                          'BL_Sales',
                                          'BL_SalesAdmin',
                                          'BL_SalesCoordinator'
                                        ]}
                                      >
                                        <ClosingCalendarPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/contracting/contract-acceptance"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLSalesAdmin,
                                          UserRoles.BLSales,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <ContractAcceptancePage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/contracting/contract-acceptance/:buildId"
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLSalesAdmin,
                                          UserRoles.BLSales,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <ContractAcceptanceDetail />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/contracting/contract-acceptance/build-details/:buildId"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/TenDayFollowup.aspx`}
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLSalesAdmin,
                                          UserRoles.BLSales,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <TenDayBuildsDetail
                                          backUrl={'/sales/contracting/contract-acceptance'}
                                          backText={'Back to Contract Acceptance'}
                                        />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/contracting/ten-day-builds"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/TenDayFollowup.aspx`}
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLSales,
                                          UserRoles.BLSalesAdmin,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <TenDayBuildsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/contracting/ten-day-builds/:buildId"
                                        legacyUrl={`${LegacySystem}/Portals/Sales/TenDayFollowup.aspx`}
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLSales,
                                          UserRoles.BLSalesAdmin,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <TenDayBuildsDetail />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/contracting/test-builds"
                                        featureFlag={FeatureFlag.salesTestBuild}
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLSales,
                                          UserRoles.BLSalesAdmin,
                                          UserRoles.BLSalesCoordinator
                                        ]}
                                      >
                                        <TestBuildsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/contracting/under-contract"
                                        featureFlag={FeatureFlag.underContract}
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLSalesAdmin,
                                          UserRoles.BLSalesCoordinator,
                                          UserRoles.BLAgent,
                                          UserRoles.BLSales
                                        ]}
                                      >
                                        <UnderContract />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/contracting/under-contract/:buildId/contract-dates"
                                        featureFlag={FeatureFlag.underContract}
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLSalesAdmin,
                                          UserRoles.BLSalesCoordinator,
                                          UserRoles.BLAgent,
                                          UserRoles.BLSales
                                        ]}
                                      >
                                        <UnderContractDetails />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/contracting/under-contract/:buildId/contract-files"
                                        featureFlag={FeatureFlag.underContract}
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLSalesAdmin,
                                          UserRoles.BLSalesCoordinator,
                                          UserRoles.BLAgent,
                                          UserRoles.BLSales
                                        ]}
                                      >
                                        <UnderContractDetails />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/contracting/under-contract/:buildId/contact-history"
                                        featureFlag={FeatureFlag.underContract}
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLSalesAdmin,
                                          UserRoles.BLSalesCoordinator,
                                          UserRoles.BLAgent,
                                          UserRoles.BLSales
                                        ]}
                                      >
                                        <UnderContractDetails />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/sales/contracting/under-contract/:buildId/address-book"
                                        featureFlag={FeatureFlag.underContract}
                                        legacyUrl={`${LegacySystem}/Portals/Sales/SalesHome.aspx`}
                                        roles={[
                                          UserRoles.BLAdmin,
                                          UserRoles.BLSalesAdmin,
                                          UserRoles.BLSalesCoordinator,
                                          UserRoles.BLAgent,
                                          UserRoles.BLSales
                                        ]}
                                      >
                                        <UnderContractDetails />
                                      </PrivateRoute>
                                      {/* Style Guide Pages */}
                                      <PrivateRoute exact path="/kitchen-sink">
                                        <StyleGuide />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/kitchen-sink/components/buttons">
                                        <StyleGuideButtons />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/kitchen-sink/components/buttons/csv-download"
                                      >
                                        <StyleGuideCSVDownloadButton />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/kitchen-sink/components/cards">
                                        <StyleGuideCards />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/kitchen-sink/components/grids">
                                        <StyleGuideGridsOverview />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/kitchen-sink/components/grids/bulk-download-grid"
                                      >
                                        <StyleGuideBulkDownloadGrid />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/kitchen-sink/components/loader">
                                        <StyleGuideLoader />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/kitchen-sink/components/modals">
                                        <StyleGuideModals />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/kitchen-sink/components/page">
                                        <StyleGuidePageComponent />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/kitchen-sink/components/page-title"
                                      >
                                        <StyleGuidePageTitle />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        exact
                                        path="/kitchen-sink/components/page-title-with-back"
                                      >
                                        <StyleGuidePageTitleWBack />
                                      </PrivateRoute>
                                      <PrivateRoute exact path="/kitchen-sink/ui-ux-patterns">
                                        <StyleGuideUIUXPatterns />
                                      </PrivateRoute>
                                      <Route
                                        exact
                                        path="/maintenance"
                                        component={MaintenanceMode}
                                      />
                                      <Route component={withTracker(NotFound)} />
                                    </Switch>
                                  </SideNavHandler>
                                  <UserAgreement />
                                </LocalizationProvider>
                              </BuildsContextHandler>
                            </DocumentContextHandler>
                          </SalesContextHandler>
                        </AdminContextHandler>
                      </VersionWrapper>
                    </NewsNotificationContextHandler>
                  </GlobalContextHandler>
                </LDWrapper>
              </UserContextHandler>
            </MaintenanceWrapper>
          </QueryClientProvider>
        </Router>
      </SnackbarProvider>
    </AppInsightsContext.Provider>
  );
}
